import {
  type IFetchAvailableProduct,
  type IConfirmProductAvailability
} from '@/types/IFetchAvailableProduct';
import { AuthService } from '../services/AuthService';
import {
  CustomError,
  getErrorData,
  IErrorData,
  type IGenericResponseRoot
} from '../utils/getErrorData';
import { api } from '../utils/api';
import { IOrderUpdateRequest } from '@/types/IOrderUpdateRequest';
import { OrderRootResponse } from '@/types/IOrder';

export const confirmProductAvailability = async (
  orderUuid: string,
  orderOfferId: number
): Promise<IGenericResponseRoot<IConfirmProductAvailability>> => {
  const url = `/admin/order/${orderUuid}/offer/${orderOfferId}/available`;

  try {
    const headers: Record<string, string> = {};

    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      await AuthService.checkUser();
      throw new Error('Пользователь не авторизирован');
    }

    const response = await api.post<
      IGenericResponseRoot<IConfirmProductAvailability>
    >(url, {}, { headers });

    if (response.status === 500) {
      throw new CustomError(
        `При подтверждении наличия товара с ID: ${orderOfferId} в заказе с ID: ${orderUuid} произошла необработанная ошибка`,
        500
      );
    }

    if (response.data.response === null) {
      throw new CustomError(
        `Не получилось получить данные о подтверждении товара ${orderOfferId} в наличии`
      );
    }

    return response.data;
  } catch (error: unknown) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error(
      `Ошибка при подтверждении наличии товара ${orderOfferId} в заказе ${orderUuid}:`,
      errorData.message
    );
    throw error;
  }
};

export const deleteProductFromOrder = async (
  orderUuid: string,
  orderOfferId: number,
  count: number
): Promise<IFetchAvailableProduct> => {
  const url = `/admin/order/${orderUuid}/offers/delete`;

  try {
    const headers: Record<string, string> = {};
    if (AuthService.authToken !== null) {
      headers.Authorization = `Bearer ${AuthService.authToken}`;
    } else {
      throw new Error('Пользователь не авторизирован');
    }

    const body = {
      offers: [
        {
          id: orderOfferId,
          count
        }
      ]
    };

    const response = await api.post<IFetchAvailableProduct>(url, body, {
      headers
    });

    if (response.data.meta?.error != null) {
      throw new Error(response.data.meta.error);
    }

    if (response.status !== 200) {
      throw new Error('Не удалось выполнить действие');
    }

    return response.data;
  } catch (error: unknown) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error(
      `Ошибка при удалении товара ${orderOfferId} из заказа ${orderUuid}:`,
      errorData.message
    );
    throw error;
  }
};

export const updateOrder = async (
  orderUuid: string,
  payload: IOrderUpdateRequest
): Promise<OrderRootResponse | IErrorData> => {
  const url = `/admin/order/${orderUuid}/update`;

  try {
    const response = await api.put<OrderRootResponse>(url, payload);

    if (response.status === 500) {
      throw new CustomError(
        'При обновлении заказа произошла необработанная ошибка'
      );
    }

    return response.data;
  } catch (error) {
    const errorData = getErrorData(error);
    // eslint-disable-next-line no-console
    console.error('Ошибка при обновлении заказа:', error);
    return errorData;
  }
};
